import { useState } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import { MapPin, MagnifyingGlass, Globe, Phone } from "@phosphor-icons/react";

export default function GoogleMapsRecherche() {
  const [keyword, setKeyword] = useState("");
  const [location, setLocation] = useState("");
  const [pages, setPages] = useState("1");
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);

  const start = async () => {
    if (!keyword.trim() || !location.trim()) {
      return toast.error("Bitte Stichwort und Region/Ort angeben");
    }
    setLoading(true);
    setResult(null);
    try {
      const { data } = await api.post("/scrape/googlemaps", {
        keyword,
        location,
        pages: Number(pages),
      });
      setResult(data);
      toast.success(`${data.found} Treffer · ${data.new} neue Leads · ${data.duplicates} Duplikate`);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Abfrage fehlgeschlagen");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="p-8 max-w-6xl">
      <div className="mb-8">
        <h1 className="font-heading text-4xl font-bold tracking-tight">Google Maps</h1>
        <p className="text-sm text-zinc-500 mt-1">
          Firmen per Stichwort und Region aus Google Maps übernehmen – inkl. Telefon, Website,
          Kategorie und Bewertung. Duplikate werden automatisch erkannt.
        </p>
      </div>

      <div className="bg-white border border-zinc-200 rounded-md p-6 mb-6">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 items-end">
          <div>
            <Label className="text-xs uppercase tracking-widest text-zinc-500">Stichwort *</Label>
            <Input value={keyword} onChange={(e) => setKeyword(e.target.value)} placeholder="z.B. Restaurant" className="mt-1.5" data-testid="gmaps-keyword" onKeyDown={(e) => e.key === "Enter" && start()} />
          </div>
          <div>
            <Label className="text-xs uppercase tracking-widest text-zinc-500">Region / Ort *</Label>
            <Input value={location} onChange={(e) => setLocation(e.target.value)} placeholder="z.B. Lüneburg" className="mt-1.5" data-testid="gmaps-location" onKeyDown={(e) => e.key === "Enter" && start()} />
          </div>
          <div>
            <Label className="text-xs uppercase tracking-widest text-zinc-500">Seiten (à ~20 Treffer)</Label>
            <Select value={pages} onValueChange={setPages}>
              <SelectTrigger className="mt-1.5 rounded-sm" data-testid="gmaps-pages"><SelectValue /></SelectTrigger>
              <SelectContent>
                {["1", "2", "3", "4", "5"].map((p) => <SelectItem key={p} value={p}>{p}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <Button onClick={start} disabled={loading} data-testid="gmaps-start-button" className="bg-[#002FA7] hover:bg-[#001D6E] text-white rounded-sm h-10">
            <MagnifyingGlass size={16} className="mr-2" />
            {loading ? "Suche läuft…" : "Suchen & übernehmen"}
          </Button>
        </div>
        <p className="text-xs text-zinc-400 mt-3">
          Quelle: Google Maps (über Serper.dev). Jede Seite verbraucht 1 API-Abfrage deines Kontingents.
        </p>
      </div>

      {result && (
        <div className="bg-white border border-zinc-200 rounded-md p-6" data-testid="gmaps-result">
          <div className="flex flex-wrap gap-3 mb-6">
            <Stat label="Gefunden" value={result.found} />
            <Stat label="Neu" value={result.new} accent />
            <Stat label="Duplikate" value={result.duplicates} muted />
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            <div data-testid="gmaps-new-leads">
              <div className="text-xs uppercase tracking-widest text-zinc-500 mb-2">Neue Leads ({result.new})</div>
              <div className="border border-zinc-200 rounded-sm max-h-96 overflow-y-auto">
                {result.leads.length === 0 ? (
                  <div className="text-xs text-zinc-400 p-3">Keine neuen Leads.</div>
                ) : (
                  <table className="w-full text-xs">
                    <tbody>
                      {result.leads.map((l, i) => (
                        <tr key={i} className="border-b border-zinc-100 last:border-0 align-top">
                          <td className="px-2 py-1.5 text-zinc-400 tabular-nums w-14">#{String(l.lead_no ?? "").padStart(4, "0")}</td>
                          <td className="px-2 py-1.5">
                            <div className="font-medium">{l.name}</div>
                            <div className="text-zinc-500">{l.street}{l.street && ", "}{l.plz} {l.city}</div>
                          </td>
                          <td className="px-2 py-1.5 text-zinc-500 whitespace-nowrap">
                            {l.phone && <div className="flex items-center gap-1"><Phone size={11} />{l.phone}</div>}
                            {l.website && <div className="flex items-center gap-1"><Globe size={11} /><span className="truncate max-w-[140px] inline-block align-bottom">{l.website.replace(/^https?:\/\/(www\.)?/, "")}</span></div>}
                          </td>
                          <td className="px-2 py-1.5 text-zinc-500 whitespace-nowrap">{l.rating != null && <>★ {l.rating} ({l.rating_count ?? 0})</>}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </div>
            </div>

            <div data-testid="gmaps-duplicate-leads">
              <div className="text-xs uppercase tracking-widest text-zinc-500 mb-2">Duplikate ({result.duplicates})</div>
              <div className="border border-zinc-200 rounded-sm max-h-96 overflow-y-auto">
                {result.duplicate_leads.length === 0 ? (
                  <div className="text-xs text-zinc-400 p-3">Keine Duplikate.</div>
                ) : (
                  <table className="w-full text-xs">
                    <tbody>
                      {result.duplicate_leads.map((l, i) => (
                        <tr key={i} className="border-b border-zinc-100 last:border-0">
                          <td className="px-2 py-1.5 text-zinc-400 tabular-nums w-14">
                            {l.existing_lead_no ? `#${String(l.existing_lead_no).padStart(4, "0")}` : "—"}
                          </td>
                          <td className="px-2 py-1.5 text-zinc-700">{l.name}</td>
                          <td className="px-2 py-1.5 text-zinc-500 whitespace-nowrap">{l.plz} {l.city}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {!result && !loading && (
        <div className="bg-white border border-dashed border-zinc-200 rounded-md p-10 text-center text-sm text-zinc-400">
          <MapPin size={28} className="mx-auto mb-2 text-zinc-300" />
          Starte eine Suche, z.B. „Restaurant" in „Lüneburg".
        </div>
      )}
    </div>
  );
}

function Stat({ label, value, accent, muted }) {
  return (
    <div className={`px-5 py-3 rounded-md border ${accent ? "border-[#002FA7] bg-[#002FA7]/5" : "border-zinc-200 bg-white"}`}>
      <div className={`font-heading text-2xl font-bold ${accent ? "text-[#002FA7]" : muted ? "text-zinc-400" : ""}`}>{value}</div>
      <div className="text-xs uppercase tracking-widest text-zinc-500">{label}</div>
    </div>
  );
}
